// Single source of truth for the CSRF token shared by the two HTTP clients in the app:
// the legacy bm-ui-kit `Http` helper (fed via AuthenticationService) and the generated
// REST client (read in restClient's request middleware).
//
// The server renders the initial token into the `_csrf` / `_csrf_header` <meta> tags, but a
// token refreshed at runtime (after login/logout, or an init refresh) is never written back
// to the DOM. Reading the meta tags directly would then serve a stale token, so callers
// record every fresh token here and read it back through getCsrfToken().

export interface CsrfToken {
	header: string;
	token: string;
}

// Spring's default CSRF header name; used when a refreshed token arrives without an explicit name.
const DEFAULT_CSRF_HEADER = "X-CSRF-TOKEN";

let currentToken: CsrfToken | undefined;

function readCsrfFromMeta(): CsrfToken | undefined {
	const header = document.querySelector('meta[name="_csrf_header"]')?.getAttribute("content");
	const token = document.querySelector('meta[name="_csrf"]')?.getAttribute("content");

	return header && token ? { header, token } : undefined;
}

/** Records a freshly obtained CSRF token so every client sends the current value. */
export function setCsrfToken(token: string, header: string = DEFAULT_CSRF_HEADER): void {
	currentToken = { header, token };
}

/** Current CSRF token, falling back to the server-rendered meta tags before any refresh. */
export function getCsrfToken(): CsrfToken | undefined {
	return currentToken ?? readCsrfFromMeta();
}

/** Test hook: clears the in-memory token so each test starts from the meta-tag fallback. */
export function resetCsrfToken(): void {
	currentToken = undefined;
}
